import { useCallback, useState } from 'react'
import { supabase } from '~/supabase-client'
import { supabaseGetter } from '~/api/supabase-getter'

/**
 * Factory hook for cascading est_started_at / est_duration updates to all
 * non-past crop phases under a farm when a frost date report is submitted.
 *
 * Delegates to the apply-frost-cascade edge function, which calls the
 * apply_frost_report_cascade Postgres RPC atomically.
 *
 * Delta semantics:
 *   new_phase_date = old_phase_date - p_old_date + p_new_date
 * i.e. the phase's offset from the frost anchor is preserved across anchor changes.
 */
export const useFrostReportCascade = () => {
  const [isCascading, setIsCascading] = useState(false)

  const runCascade = useCallback(async ({ farmId, reportCode, newDate, oldDate }) => {
    if (!oldDate || !newDate || !farmId) return

    setIsCascading(true)
    try {
      const { data, error } = await supabase.functions.invoke('frost-cascade', {
        body: {
          farmId,
          reportCode,
          newDate: newDate instanceof Date ? newDate.toISOString() : newDate,
          oldDate: oldDate instanceof Date ? oldDate.toISOString() : oldDate,
        },
      })
      if (error || !data?.success) {
        const msg = data?.message ?? error?.message ?? 'Frost cascade failed'
        console.error('Frost report cascade failed:', msg)
        throw new Error(msg)
      }

      // Invalidate caches so timeline and schedule views reflect updated est_ values
      supabaseGetter.cropSchedules.invalidate()
      supabaseGetter.cropPhases.invalidate()
      supabaseGetter.tasks.invalidate()
    } finally {
      setIsCascading(false)
    }
  }, [])

  return { runCascade, isCascading }
}
